import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ClientTable from './components/ClientTable';
import FilterControls from './components/FilterControls';
import BulkActions from './components/BulkActions';
import BulkEditModal from './components/BulkEditModal';
import ImportWizard from './components/ImportWizard';
import ClientStats from './components/ClientStats';
import AddClientForm from './components/AddClientForm';
import { useAuth } from '../../contexts/AuthContext';
import { getClients, getClientStats, createClient, updateClient, addClientInteraction, bulkAddTags, bulkUpdateClients, bulkDeleteClients, exportClientsData, subscribeToClients, importClientsFromCSV } from '../../utils/clientService';
import conversationHelper from '../../lib/conversation-helper';

const ClientManagement = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState({
    totalClients: 0,
    activeLeads: 0,
    underContract: 0,
    closedThisMonth: 0
  });
  const [selectedClients, setSelectedClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [lifecycleFilter, setLifecycleFilter] = useState('');
  const [engagementFilter, setEngagementFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [isImportWizardOpen, setIsImportWizardOpen] = useState(false);
  const [isAddClientOpen, setIsAddClientOpen] = useState(false);
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Refs to prevent multiple API calls and memory leaks
  const loadingRef = useRef(false);
  const unsubscribeRef = useRef(null);
  const timeoutRef = useRef(null);

  // Memoized data loading function to prevent infinite loops
  const loadClientsData = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (loadingRef?.current || !isAuthenticated || authLoading) {
      return;
    }

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    // Set a timeout to prevent infinite loading
    timeoutRef.current = setTimeout(() => {
      if (loadingRef?.current) {
        setError('Loading timeout. Please refresh the page.');
        setLoading(false);
        loadingRef.current = false;
      }
    }, 30000); // 30 second timeout

    try {
      const [clientsResponse, statsResponse] = await Promise.all([
        getClients(),
        getClientStats()
      ]);

      // Clear timeout if successful
      if (timeoutRef?.current) {
        clearTimeout(timeoutRef?.current);
        timeoutRef.current = null;
      }

      if (clientsResponse?.error) {
        setError(clientsResponse?.error?.message || 'Failed to load clients');
      } else {
        setClients(clientsResponse?.data || []);
      }

      if (statsResponse?.error) {
        console.error('Stats error:', statsResponse?.error?.message);
        // Don't show error for stats, just log it
      } else {
        setStats(statsResponse?.data || stats);
      }
    } catch (err) {
      console.error('Load clients error:', err);
      setError('Failed to load client data. Please check your connection and try again.');
    } finally {
      setLoading(false);
      loadingRef.current = false;
      
      // Clear timeout if it's still set
      if (timeoutRef?.current) {
        clearTimeout(timeoutRef?.current);
        timeoutRef.current = null;
      }
    }
  }, [isAuthenticated, authLoading]);

  // Initial data load - only when authentication is complete
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      loadClientsData();
    } else if (!authLoading && !isAuthenticated) {
      // User is not authenticated, clear data and stop loading
      setLoading(false);
      setClients([]);
      setStats({
        totalClients: 0,
        activeLeads: 0,
        underContract: 0,
        closedThisMonth: 0
      });
    }
  }, [isAuthenticated, authLoading, loadClientsData]);

  // Real-time subscription - separate from data loading to prevent loops
  useEffect(() => {
    if (!isAuthenticated || authLoading) return;

    // Clean up previous subscription
    if (unsubscribeRef?.current) {
      unsubscribeRef?.current();
      unsubscribeRef.current = null;
    }

    // Set up new subscription
    unsubscribeRef.current = subscribeToClients((payload) => {
      // Only refresh data if we're not currently loading
      if (!loadingRef?.current) {
        // Use a small delay to batch rapid changes
        if (timeoutRef?.current) {
          clearTimeout(timeoutRef?.current);
        }
        
        timeoutRef.current = setTimeout(() => {
          loadClientsData();
        }, 1000);
      }
    });

    // Cleanup function
    return () => {
      if (unsubscribeRef?.current) {
        unsubscribeRef?.current();
        unsubscribeRef.current = null;
      }
      if (timeoutRef?.current) {
        clearTimeout(timeoutRef?.current);
        timeoutRef.current = null;
      }
    };
  }, [isAuthenticated, authLoading, loadClientsData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (unsubscribeRef?.current) {
        unsubscribeRef?.current();
      }
      if (timeoutRef?.current) {
        clearTimeout(timeoutRef?.current);
      }
      loadingRef.current = false;
    };
  }, []);

  // Filter and sort clients
  const filteredAndSortedClients = useMemo(() => {
    if (!clients?.length) return [];

    let filtered = clients?.filter((client) => {
      const matchesSearch = !searchQuery ||
        client?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        client?.phone?.includes(searchQuery);

      const matchesLifecycle = !lifecycleFilter || client?.lifecycle_stage === lifecycleFilter;
      const matchesEngagement = !engagementFilter || client?.engagement_level === engagementFilter;
      const matchesLocation = !locationFilter || client?.location === locationFilter;
      const matchesTag = !tagFilter || client?.tags?.includes(tagFilter);

      return matchesSearch && matchesLifecycle && matchesEngagement && matchesLocation && matchesTag;
    });

    // Sort clients
    filtered?.sort((a, b) => {
      let aValue = a?.[sortConfig?.key];
      let bValue = b?.[sortConfig?.key];

      if (sortConfig?.key === 'last_interaction') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [clients, searchQuery, lifecycleFilter, engagementFilter, locationFilter, tagFilter, sortConfig]);

  const hasActiveFilters = searchQuery || lifecycleFilter || engagementFilter || locationFilter || tagFilter;

  const handleSelectClient = useCallback((clientId) => {
    setSelectedClients((prev) =>
      prev?.includes(clientId) ?
        prev?.filter((id) => id !== clientId) :
        [...prev, clientId]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedClients?.length === filteredAndSortedClients?.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(filteredAndSortedClients?.map((client) => client?.id));
    }
  }, [selectedClients?.length, filteredAndSortedClients]);

  const handleSort = useCallback((key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchQuery('');
    setLifecycleFilter('');
    setEngagementFilter('');
    setLocationFilter('');
    setTagFilter('');
  }, []);

  const handleRetry = useCallback(() => {
    setError(null);
    loadClientsData();
  }, [loadClientsData]);

  const handleEditClient = async (client) => {
    try {
      console.log('Edit client:', client);
      
      const updates = {
        notes: client?.notes + ' - Updated via edit'
      };
      
      const { error } = await updateClient(client?.id, updates);
      if (error) {
        setError(error?.message);
      } else {
        // Don't reload all data, just refresh in the background
        setTimeout(() => loadClientsData(), 1000);
      }
    } catch (err) {
      setError('Failed to update client. Please try again.');
    }
  };

  const handleMessageClient = useCallback(async (client) => {
    try {
      if (!client?.phone) {
        setError('Client has no phone number. Please add a phone number first.');
        return;
      }

      // Check if client can receive SMS
      if (!conversationHelper.canClientReceiveSMS(client)) {
        setError('Client phone number is invalid. Please add a valid phone number.');
        return;
      }

      // Start or get existing conversation
      const result = await conversationHelper.startConversationWithClient(client.id, user.id);

      if (result.success) {
        // Navigate to conversations page with the conversation
        navigate('/conversations', { 
          state: { 
            conversationId: result.conversationId,
            clientName: client.name
          }
        });
      } else {
        setError('Failed to start conversation: ' + (result.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error starting conversation:', err);
      setError('Failed to start conversation. Please try again.');
    }
  }, [user, navigate]);

  const handleAddNote = async (client) => {
    try {
      const noteData = {
        interaction_type: 'Phone call',
        notes: 'Quick note added from client management',
        completed_at: new Date()?.toISOString()
      };

      const { error } = await addClientInteraction(client?.id, noteData);
      if (error) {
        setError(error?.message);
      } else {
        setTimeout(() => loadClientsData(), 1000);
      }
    } catch (err) {
      setError('Failed to add note. Please try again.');
    }
  };

  const handleBulkTag = async () => {
    if (!selectedClients?.length) return;

    try {
      const tags = ['VIP'];
      const { error } = await bulkAddTags(selectedClients, tags);
      
      if (error) {
        setError(error?.message);
      } else {
        setTimeout(() => loadClientsData(), 1000);
        setSelectedClients([]);
      }
    } catch (err) {
      setError('Failed to add tags. Please try again.');
    }
  };

  const handleBulkDelete = useCallback(async () => {
    if (!selectedClients?.length) return;
    
    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedClients.length} client(s)? This action cannot be undone.`
    );
    
    if (!confirmDelete) return;

    try {
      const { error } = await bulkDeleteClients(selectedClients);
      
      if (error) {
        setError(error?.message || 'Failed to delete clients');
      } else {
        setSelectedClients([]);
        setTimeout(() => loadClientsData(), 500);
      }
    } catch (err) {
      setError('Failed to delete clients. Please try again.');
    }
  }, [selectedClients, loadClientsData]);

  const handleBulkEdit = useCallback(() => {
    if (!selectedClients?.length) return;
    setIsBulkEditOpen(true);
  }, [selectedClients]);

  const handleBulkExport = async () => {
    if (!selectedClients?.length) return;

    try {
      const { data, error } = await exportClientsData(selectedClients);
      
      if (error) {
        setError(error?.message);
        return;
      }

      const csvContent = convertToCSV(data);
      downloadCSV(csvContent, 'clients_export.csv');
      setSelectedClients([]);
    } catch (err) {
      setError('Failed to export clients. Please try again.');
    }
  };

  const convertToCSV = (data) => {
    if (!data?.length) return '';

    const headers = Object.keys(data?.[0]);
    const csvRows = [
      headers?.join(','),
      ...data?.map((row) => 
        headers?.map((header) => {
          const value = row?.[header] || '';
          return `"${value?.toString()?.replace(/"/g, '""')}"`;
        })?.join(',')
      )
    ];

    return csvRows?.join('\n');
  };

  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link?.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link?.setAttribute('href', url);
      link?.setAttribute('download', filename);
      link?.style && (link.style.visibility = 'hidden');
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
    }
  };

  const handleImport = async (importData) => {
    try {
      // Past-clients wizard already persisted — just show results
      if (importData?.__pastClientsResults) {
        const results = importData.__pastClientsResults;
        const successMessage = [];
        if (results.successful > 0) {
          successMessage.push(`${results.successful} past client(s) imported`);
        }
        if (results.updated > 0) {
          successMessage.push(`${results.updated} client(s) updated`);
        }
        if (results.failed > 0) {
          successMessage.push(`${results.failed} failed`);
        }
        if (results.successful > 0 || results.updated > 0) {
          setTimeout(() => loadClientsData(), 500);
          const phoneWarning = results.missingPhone > 0
            ? `\n\n⚠ ${results.missingPhone} contact(s) have no phone number and won't receive texts until one is added.`
            : '';
          alert((successMessage.join(', ') || 'Import complete') + phoneWarning);
        } else if (results.failed > 0) {
          setError(`Import completed with errors: ${(results.errors || []).join('; ')}`);
        }
        setIsImportWizardOpen(false);
        return;
      }

      console.log('Import clients:', importData);
      
      const { data: results, error: importError } = await importClientsFromCSV(importData);

      if (importError) {
        setError(importError.message);
        throw new Error(importError.message);
      }

      // Show success message
      const successMessage = [];
      if (results.successful > 0) {
        successMessage.push(`${results.successful} client(s) imported successfully`);
      }
      if (results.updated > 0) {
        successMessage.push(`${results.updated} client(s) updated`);
      }
      if (results.failed > 0) {
        successMessage.push(`${results.failed} client(s) failed`);
      }

      if (results.successful > 0 || results.updated > 0) {
        // Reload client data after successful import
        setTimeout(() => loadClientsData(), 500);
        alert(successMessage.join(', '));
      } else if (results.failed > 0) {
        setError(`Import completed with errors: ${results.errors.join('; ')}`);
      }

      setIsImportWizardOpen(false);
    } catch (err) {
      console.error('Import error:', err);
      // Error is already set in the catch block above
    }
  };

  // Show authentication loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16 pb-20 md:pb-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Authenticating...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Show not authenticated state
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16 pb-20 md:pb-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Icon name="Lock" size={48} className="mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">Authentication Required</h3>
                <p className="text-muted-foreground">Please log in to access client management.</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Show loading state with better UX
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16 pb-20 md:pb-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading clients...</p>
                <p className="text-sm text-muted-foreground mt-2">This may take a few moments</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Show error state with retry option
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16 pb-20 md:pb-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 mb-6 max-w-md mx-auto">
              <div className="flex items-center mb-4">
                <Icon name="AlertCircle" size={24} className="text-destructive mr-3" />
                <h3 className="text-destructive font-medium text-lg">Unable to Load Clients</h3>
              </div>
              <p className="text-destructive/80 mb-4">{error}</p>
              <div className="flex space-x-3">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleRetry}
                  disabled={loading}
                  className="flex-1"
                >
                  {loading ? 'Retrying...' : 'Try Again'}
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setError(null)}
                  className="flex-1"
                >
                  Dismiss
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16 pb-20 md:pb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold text-foreground tracking-tight">Client Management</h1>
              <p className="text-muted-foreground mt-2">
                Organize and nurture your client relationships for long-term success
              </p>
            </div>
            
            <div className="flex items-center space-x-3 mt-4 sm:mt-0">
              <Button
                variant="outline"
                onClick={() => setIsImportWizardOpen(true)}
                iconName="Upload"
                iconPosition="left">
                Import Clients
              </Button>
              
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={() => setIsAddClientOpen(true)}>
                Add Client
              </Button>
            </div>
          </div>

          {/* Stats */}
          <ClientStats stats={stats} />

          {/* Filter Controls */}
          <FilterControls
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            lifecycleFilter={lifecycleFilter}
            onLifecycleFilterChange={setLifecycleFilter}
            engagementFilter={engagementFilter}
            onEngagementFilterChange={setEngagementFilter}
            locationFilter={locationFilter}
            onLocationFilterChange={setLocationFilter}
            tagFilter={tagFilter}
            onTagFilterChange={setTagFilter}
            onClearFilters={handleClearFilters}
            hasActiveFilters={hasActiveFilters} 
          />

          {/* Bulk Actions */}
          <BulkActions
            selectedCount={selectedClients?.length}
            onBulkTag={handleBulkTag}
            onBulkEdit={handleBulkEdit}
            onBulkDelete={handleBulkDelete}
            onBulkExport={handleBulkExport}
            onClearSelection={() => setSelectedClients([])} 
          />

          {/* Results Summary */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">
              Showing {filteredAndSortedClients?.length} of {clients?.length} clients
              {hasActiveFilters && ' (filtered)'}
            </p>
            
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">Sort by:</span>
              <span className="text-sm font-medium text-foreground capitalize">
                {sortConfig?.key} ({sortConfig?.direction})
              </span>
            </div>
          </div>

          {/* Client Table */}
          <ClientTable
            clients={filteredAndSortedClients}
            selectedClients={selectedClients}
            onSelectClient={handleSelectClient}
            onSelectAll={handleSelectAll}
            onEditClient={handleEditClient}
            onMessageClient={handleMessageClient}
            onAddNote={handleAddNote}
            sortConfig={sortConfig}
            onSort={handleSort} 
          />

          {/* Empty State */}
          {filteredAndSortedClients?.length === 0 && !loading && (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">
                {hasActiveFilters ? 'No clients match your filters' : 'No clients found'}
              </h3>
              <p className="text-muted-foreground mb-6">
                {hasActiveFilters ?
                  'Try adjusting your search criteria or clearing filters.' : 'Get started by adding your first client or importing from your existing CRM.'
                }
              </p>
              {hasActiveFilters ? (
                <Button variant="outline" onClick={handleClearFilters}>
                  Clear Filters
                </Button>
              ) : (
                <div className="flex items-center justify-center space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setIsImportWizardOpen(true)}
                    iconName="Upload"
                    iconPosition="left">
                    Import Clients
                  </Button>
                  <Button
                    variant="default"
                    iconName="Plus"
                    iconPosition="left"
                    onClick={() => setIsAddClientOpen(true)}>
                    Add Client
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Import Wizard */}
      <ImportWizard
        isOpen={isImportWizardOpen}
        onClose={() => setIsImportWizardOpen(false)}
        onImport={handleImport} 
      />

      {/* Add Client Form */}
      <AddClientForm
        isOpen={isAddClientOpen}
        onClose={() => setIsAddClientOpen(false)}
        onSuccess={(newClient) => {
          setIsAddClientOpen(false);
          loadClientsData();
        }}
      />

      {/* Bulk Edit Modal */}
      <BulkEditModal
        isOpen={isBulkEditOpen}
        onClose={() => setIsBulkEditOpen(false)}
        selectedClientIds={selectedClients}
        onSuccess={() => {
          setSelectedClients([]);
          setIsBulkEditOpen(false);
          setTimeout(() => loadClientsData(), 500);
        }}
      />
    </div>
  );
};

export default ClientManagement;